import { MouseEvent } from 'react';
import type { TableState } from '@tanstack/react-table';

import Linkv2 from '@/components/content/link/Linkv2';
import SeeMoreLink from '@/components/content/link/SeeMoreLink';
import IssueIDLink from '../../issueIdLink/IssueIdLink';
import IssueWrapperV2 from '../../issue/components/IssueWrapper';

import { useModal } from '@/hooks/useModal';
import { useMSSP } from '@/hooks/useIsMSSP';
import { incidentModalModules } from '../configs/incident.modal';
import ExpandIcon from '@/assets/icons/content/expand.svg';

import { MercuryTrackersIds, mercuryTrackers } from '@/constants/mercuryPages';
import type { IncidentPageTableRecord } from '../types';
import { incidentParentCategory } from '@/client/features/incidentmanagement/utils';
import classes from './IncidentModal.module.css';

interface IncidentModalProps {
  tableState?: TableState;
  incident: IncidentPageTableRecord;
  inSearchResults?: boolean;
}

const IncidentModal = ({ incident, tableState, inSearchResults }: IncidentModalProps) => {
  const { Modal, modalProps, openModal } = useModal();
  const { isSubclient } = useMSSP();
  const issueId = incident.id;

  const clickHandler = (e: MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();

    openModal();
  };

  return (
    <div>
      <Linkv2
        href={`/${incidentParentCategory(incident)}/alert/issue/${incident.id}`}
        onClick={clickHandler}
      >
        {issueId} {inSearchResults ? `| ${incident.name}` : ''}
      </Linkv2>

      <Modal
        {...modalProps}
        title={`${issueId} | ${incident.name}`}
        size="xl"
        linkPosition="close"
        classNames={{
          content: classes.modalContent,
          header: classes.modalHeader,
          title: classes.modalTitle,
          close: classes.modalClose,
          body: classes.modalBodyShell,
        }}
        link={
          <SeeMoreLink>
            <IssueIDLink
              value={issueId}
              name={
                <>
                  <span>See more</span>
                  <ExpandIcon />
                </>
              }
              tracker={{
                ...mercuryTrackers[MercuryTrackersIds.alerts],
                parent: incidentParentCategory(incident),
              }}
              tableState={tableState}
              usePage
              useParent={inSearchResults}
            />
          </SeeMoreLink>
        }
        centered
        alternativeBackground
      >
        <div className={classes.modalBody}>
          <IssueWrapperV2
            top={incidentModalModules({ hideActions: isSubclient || inSearchResults })}
            data={incident}
            id={issueId}
            removePadding
          />
        </div>
      </Modal>
    </div>
  );
};

export default IncidentModal;
